import axios from "axios";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import Swal from "sweetalert2";


// ======================================================
// API
// ======================================================

const API = "/api/auditoria/asignacion";

const API_AUDITORES =
  "/api/auditoria/asignacion/auditores-disponibles";

const API_INSTRUCTORES =
  "/api/instructores/listall";


// ======================================================
// CONFIGURACIÓN AUTH
// ======================================================

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${
      localStorage.getItem("token") || ""
    }`,
  },
});


// ======================================================
// UTILIDAD - NOMBRE COMPLETO
// ======================================================

const nombreInstructor = (instructor) =>
  [
    instructor?.nombre1Instructor,
    instructor?.nombre2Instructor,
    instructor?.apellido1Instructor,
    instructor?.apellido2Instructor,
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();


// ======================================================
// UTILIDAD - FECHA PARA INPUT
// ======================================================

const toInputDate = (valor) => {
  if (!valor) return "";

  const fecha = new Date(valor);

  if (Number.isNaN(fecha.getTime())) {
    return "";
  }

  return fecha
    .toISOString()
    .slice(0, 10);
};


// ======================================================
// ESTADO INICIAL
// ======================================================

const ESTADO_INICIAL = {
  auditorObjectId: "",
  instructorObjectId: "",
  fechaInicioAsignacion: "",
  fechaFinAsignacion: "",
  estadoAsignacion: "Activo",
  observacionAsignacion: "",
};


// ======================================================
// COMPONENTE
// ======================================================

const Auditoria_RegistrarAsignacion = () => {

  const navigate = useNavigate();

  const { id } = useParams();


  // ====================================================
  // EDITANDO
  // ====================================================

  const editando = useMemo(
    () => Boolean(id),
    [id]
  );


  // ====================================================
  // ESTADOS
  // ====================================================

  const [form, setForm] =
    useState(ESTADO_INICIAL);

  const [auditores, setAuditores] =
    useState([]);

  const [instructores, setInstructores] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [cargando, setCargando] =
    useState(false);


  // ====================================================
  // MANEJO DE ERRORES
  // ====================================================

  const manejarError = useCallback(
    (error, mensaje) => {

      console.error(
        "Error Auditoría Asignación:",
        error
      );

      if (
        error?.response?.status === 401
      ) {

        localStorage.removeItem("token");

        Swal.fire(
          "Sesión expirada",
          "Inicia sesión nuevamente.",
          "warning"
        ).then(() => {
          navigate("/login");
        });

        return;
      }


      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          mensaje ||
          "Ocurrió un error.",
        "error"
      );
    },
    [navigate]
  );


  // ====================================================
  // CARGAR DATOS BASE
  // ====================================================

  const cargarBase = useCallback(
    async () => {

      try {

        setCargando(true);


        // ================================================
        // CONSULTAS BASE
        // ================================================

        const peticiones = [

          axios.get(
            API_AUDITORES,
            authConfig()
          ),

          axios.get(
            API_INSTRUCTORES,
            authConfig()
          ),

        ];


        // ================================================
        // SI ESTAMOS EDITANDO
        // CONSULTAR ASIGNACIÓN
        // ================================================

        if (editando) {

          peticiones.push(
            axios.get(
              `${API}/${id}`,
              authConfig()
            )
          );
        }


        const respuestas =
          await Promise.all(peticiones);


        // ================================================
        // AUDITORES DISPONIBLES
        // ================================================

        const respuestaAuditores =
          respuestas[0]?.data;


        const listaAuditores =
          Array.isArray(
            respuestaAuditores?.auditores
          )
            ? respuestaAuditores.auditores
            : [];


        setAuditores(
          listaAuditores
        );


        // ================================================
        // INSTRUCTORES
        // ================================================

        const respuestaInstructores =
          respuestas[1]?.data;


        const listaInstructores =
          Array.isArray(
            respuestaInstructores?.instructores
          )
            ? respuestaInstructores.instructores
            : [];


        setInstructores(
          listaInstructores
        );


        // ================================================
        // CARGAR ASIGNACIÓN SI ESTAMOS EDITANDO
        // ================================================

        if (editando) {

          const item =
            respuestas[2]?.data?.asignacion;


          if (!item) {

            throw new Error(
              "Asignación no encontrada."
            );
          }


          // ==============================================
          // AUDITOR ID
          //
          // Puede venir:
          // - como ObjectId/string
          // - como objeto populado
          // ==============================================

          const auditorId =
            typeof item.auditorObjectId ===
            "object"
              ? item.auditorObjectId?._id
              : item.auditorObjectId;


          // ==============================================
          // INSTRUCTOR ID
          // ==============================================

          const instructorId =
            typeof item.instructorObjectId ===
            "object"
              ? item.instructorObjectId?._id
              : item.instructorObjectId;


          setForm({

            auditorObjectId:
              auditorId || "",

            instructorObjectId:
              instructorId || "",

            fechaInicioAsignacion:
              toInputDate(
                item.fechaInicioAsignacion
              ),

            fechaFinAsignacion:
              toInputDate(
                item.fechaFinAsignacion
              ),

            estadoAsignacion:
              item.estadoAsignacion ||
              "Activo",

            observacionAsignacion:
              item.observacionAsignacion ||
              "",

          });
        }

      } catch (error) {

        manejarError(
          error,
          "No se pudieron cargar los datos."
        );

      } finally {

        setCargando(false);

      }

    },
    [
      editando,
      id,
      manejarError,
    ]
  );


  // ====================================================
  // CARGAR AL INICIAR
  // ====================================================

  useEffect(() => {

    cargarBase();

  }, [cargarBase]);


  // ====================================================
  // CAMBIAR FORMULARIO
  // ====================================================

  const cambiar = (
    campo,
    valor
  ) => {

    setForm((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };


  // ====================================================
  // AUDITOR SELECCIONADO
  // ====================================================

  const auditorSeleccionado =
    useMemo(() => {

      return auditores.find(
        (auditor) =>
          String(auditor?._id) ===
          String(form.auditorObjectId)
      );

    }, [
      auditores,
      form.auditorObjectId,
    ]);


  // ====================================================
  // INSTRUCTOR SELECCIONADO
  // ====================================================

  const instructorSeleccionado =
    useMemo(() => {

      return instructores.find(
        (instructor) =>
          String(instructor?._id) ===
          String(
            form.instructorObjectId
          )
      );

    }, [
      instructores,
      form.instructorObjectId,
    ]);


  // ====================================================
  // GUARDAR
  // ====================================================

  const guardar = async (e) => {

    e.preventDefault();


    // ==================================================
    // VALIDACIONES
    // ==================================================

    if (!form.auditorObjectId) {

      Swal.fire(
        "Atención",
        "Debe seleccionar un auditor.",
        "warning"
      );

      return;
    }


    if (!form.instructorObjectId) {

      Swal.fire(
        "Atención",
        "Debe seleccionar un instructor.",
        "warning"
      );

      return;
    }


    try {

      setLoading(true);


      // ==================================================
      // PAYLOAD
      // ==================================================

      const payload = {

        auditorObjectId:
          form.auditorObjectId,

        instructorObjectId:
          form.instructorObjectId,

        fechaInicioAsignacion:
          form.fechaInicioAsignacion ||
          undefined,

        fechaFinAsignacion:
          form.fechaFinAsignacion ||
          undefined,

        estadoAsignacion:
          form.estadoAsignacion,

        observacionAsignacion:
          form.observacionAsignacion,

      };


      // ==================================================
      // ACTUALIZAR
      // ==================================================

      if (editando) {

        await axios.put(
          `${API}/${id}`,
          payload,
          authConfig()
        );

      }

      // ==================================================
      // CREAR
      // ==================================================

      else {

        await axios.post(
          API,
          payload,
          authConfig()
        );

      }


      await Swal.fire(
        "Correcto",
        editando
          ? "Asignación actualizada correctamente."
          : "Asignación registrada correctamente.",
        "success"
      );


      navigate(
        "/auditoria/asignacion"
      );

    } catch (error) {

      manejarError(
        error,
        "No se pudo guardar la asignación."
      );

    } finally {

      setLoading(false);

    }
  };


  // ====================================================
  // RENDER
  // ====================================================

  return (

    <div className="container py-4">

      <div className="card shadow-sm">


        {/* ===============================================
            ENCABEZADO
        =============================================== */}

        <div className="card-header">

          <h4 className="mb-0">

            {editando
              ? "Actualizar asignación de auditoría"
              : "Registrar asignación de auditoría"}

          </h4>

        </div>


        {/* ===============================================
            FORMULARIO
        =============================================== */}

        <form onSubmit={guardar}>


          <div className="card-body">


            {/* ===========================================
                CARGANDO
            =========================================== */}

            {cargando ? (

              <div className="text-center py-4">

                <div
                  className="spinner-border"
                  role="status"
                >
                  <span className="visually-hidden">
                    Cargando...
                  </span>
                </div>

                <div className="mt-2">
                  Cargando información...
                </div>

              </div>

            ) : (

              <div className="row g-3">


                {/* =======================================
                    AUDITOR
                ======================================= */}

                <div className="col-md-6">

                  <label className="form-label">
                    Auditor *
                  </label>


                  <select
                    className="form-select"
                    value={
                      form.auditorObjectId
                    }
                    onChange={(e) =>
                      cambiar(
                        "auditorObjectId",
                        e.target.value
                      )
                    }
                    required
                  >

                    <option value="">
                      Seleccione un auditor...
                    </option>


                    {auditores.map(
                      (auditor) => {

                        const nombre =
                          auditor.nombreCompleto ||
                          nombreInstructor(
                            auditor
                          ) ||
                          "Sin nombre";


                        const rol =
                          auditor.nombreRol ||
                          auditor
                            ?.rolObjectId
                            ?.nombreRol ||
                          "Sin rol";


                        const documento =
                          auditor.documentoInstructor ||
                          "Sin documento";


                        return (

                          <option
                            key={
                              auditor._id
                            }
                            value={
                              auditor._id
                            }
                          >

                            {nombre}
                            {" - "}
                            {rol}
                            {" - "}
                            {documento}

                          </option>

                        );
                      }
                    )}

                  </select>


                  <small className="text-muted">

                    Se muestran únicamente
                    personas cuyo rol tiene
                    permisos de lectura y
                    creación sobre el módulo
                    Auditoria.

                  </small>

                </div>


                {/* =======================================
                    INSTRUCTOR
                ======================================= */}

                <div className="col-md-6">

                  <label className="form-label">
                    Instructor *
                  </label>


                  <select
                    className="form-select"
                    value={
                      form.instructorObjectId
                    }
                    onChange={(e) =>
                      cambiar(
                        "instructorObjectId",
                        e.target.value
                      )
                    }
                    required
                  >

                    <option value="">
                      Seleccione un instructor...
                    </option>


                    {instructores.map(
                      (instructor) => (

                        <option
                          key={
                            instructor._id
                          }
                          value={
                            instructor._id
                          }
                        >

                          {nombreInstructor(
                            instructor
                          ) || "Sin nombre"}

                          {" - "}

                          {instructor
                            .documentoInstructor ||
                            "Sin documento"}

                        </option>

                      )
                    )}

                  </select>

                </div>


                {/* =======================================
                    INFORMACIÓN DEL AUDITOR
                ======================================= */}

                {auditorSeleccionado && (

                  <div className="col-12">

                    <div className="alert alert-light border mb-0">

                      <div className="row g-2">


                        <div className="col-md-4">

                          <strong>
                            Auditor:
                          </strong>

                          <br />

                          {auditorSeleccionado
                            .nombreCompleto ||
                            nombreInstructor(
                              auditorSeleccionado
                            )}

                        </div>


                        <div className="col-md-3">

                          <strong>
                            Rol:
                          </strong>

                          <br />

                          {auditorSeleccionado
                            .nombreRol ||
                            auditorSeleccionado
                              ?.rolObjectId
                              ?.nombreRol ||
                            "Sin rol"}

                        </div>


                        <div className="col-md-3">

                          <strong>
                            Documento:
                          </strong>

                          <br />

                          {auditorSeleccionado
                            .documentoInstructor ||
                            "Sin documento"}

                        </div>


                        <div className="col-md-2">

                          <strong>
                            Permisos:
                          </strong>

                          <br />

                          <span
                            className="badge text-bg-success me-1"
                          >
                            Leer
                          </span>

                          <span
                            className="badge text-bg-success"
                          >
                            Crear
                          </span>

                        </div>

                      </div>

                    </div>

                  </div>

                )}


                {/* =======================================
                    INFORMACIÓN DEL INSTRUCTOR
                ======================================= */}

                {instructorSeleccionado && (

                  <div className="col-12">

                    <div className="alert alert-light border mb-0">

                      <strong>
                        Instructor seleccionado:
                      </strong>

                      {" "}

                      {nombreInstructor(
                        instructorSeleccionado
                      )}

                      {" - "}

                      {instructorSeleccionado
                        .documentoInstructor ||
                        "Sin documento"}

                    </div>

                  </div>

                )}


                {/* =======================================
                    FECHA INICIO
                ======================================= */}

                <div className="col-md-4">

                  <label className="form-label">
                    Fecha inicio
                  </label>

                  <input
                    type="date"
                    className="form-control"
                    value={
                      form.fechaInicioAsignacion
                    }
                    onChange={(e) =>
                      cambiar(
                        "fechaInicioAsignacion",
                        e.target.value
                      )
                    }
                  />

                </div>


                {/* =======================================
                    FECHA FIN
                ======================================= */}

                <div className="col-md-4">

                  <label className="form-label">
                    Fecha fin
                  </label>

                  <input
                    type="date"
                    className="form-control"
                    value={
                      form.fechaFinAsignacion
                    }
                    onChange={(e) =>
                      cambiar(
                        "fechaFinAsignacion",
                        e.target.value
                      )
                    }
                  />

                </div>


                {/* =======================================
                    ESTADO
                ======================================= */}

                <div className="col-md-4">

                  <label className="form-label">
                    Estado
                  </label>

                  <select
                    className="form-select"
                    value={
                      form.estadoAsignacion
                    }
                    onChange={(e) =>
                      cambiar(
                        "estadoAsignacion",
                        e.target.value
                      )
                    }
                  >

                    <option value="Activo">
                      Activo
                    </option>

                    <option value="Finalizado">
                      Finalizado
                    </option>

                  </select>

                </div>


                {/* =======================================
                    OBSERVACIÓN
                ======================================= */}

                <div className="col-12">

                  <label className="form-label">
                    Observación
                  </label>

                  <textarea
                    className="form-control"
                    rows="4"
                    value={
                      form.observacionAsignacion
                    }
                    onChange={(e) =>
                      cambiar(
                        "observacionAsignacion",
                        e.target.value
                      )
                    }
                    placeholder="Observación de la asignación..."
                  />

                </div>

              </div>

            )}

          </div>


          {/* =============================================
              BOTONES
          ============================================= */}

          <div className="card-footer d-flex justify-content-end gap-2">

            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() =>
                navigate(
                  "/auditoria/asignacion"
                )
              }
            >
              Cancelar
            </button>


            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                loading ||
                cargando
              }
            >

              {loading
                ? "Guardando..."
                : editando
                  ? "Actualizar"
                  : "Guardar"}

            </button>

          </div>


        </form>

      </div>

    </div>
  );
};


export default Auditoria_RegistrarAsignacion;